import { createPreferencesApi } from '@d-orca/shared/client/apis';
import { authedFetch, UnauthorizedError } from '@apis/auth';
import type { CardId, DashboardLayoutItem } from '@interfaces/IDashboardLayout';
import type { BriefSectionKey } from '@constants/briefSections';

// FE-tier mirror of the backend's `core/preferences/types.ts` NamedLayout.
// `items[].i` is the CardId string-literal union here, while the backend domain
// deliberately keeps it a plain string (the allowlist is enforced at its Zod
// boundary). Do not try to share this type across the API boundary — the two
// tiers are intentionally different strictness levels.
export type NamedLayout = {
  id: string;
  name: string;
  items: DashboardLayoutItem[];
  createdAt: string;
  updatedAt: string;
};

export type LayoutsResponse = { layouts: NamedLayout[]; activeLayoutId: string };

const BASE = '/api/me/dashboard-layouts';
const AI_AGENT_BASE = '/api/me/ai-agent-preferences';

// `T` is asserted, not validated at runtime — callers rely on the backend's
// Zod response schemas being the single source of shape truth (the standard
// pattern across `apis/`).
async function jsonOrThrow<T>(res: Response, ctx: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  if (res.status === 401) throw new UnauthorizedError();
  const body = await res.json().catch(() => null);
  throw new Error(`${ctx} failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export async function fetchLayouts(signal?: AbortSignal): Promise<LayoutsResponse> {
  return jsonOrThrow(await authedFetch(BASE, { signal }), 'fetch layouts');
}

export async function createLayout(name: string, items: DashboardLayoutItem[]): Promise<NamedLayout> {
  return jsonOrThrow(await authedFetch(BASE, { method: 'POST', body: JSON.stringify({ name, items }) }), 'create layout');
}

export async function updateLayout(
  id: string,
  patch: { name?: string; items?: DashboardLayoutItem[] },
): Promise<NamedLayout> {
  return jsonOrThrow(
    await authedFetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    'update layout',
  );
}

export async function deleteLayout(id: string): Promise<{ activeLayoutId: string }> {
  return jsonOrThrow(
    await authedFetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    'delete layout',
  );
}

export async function setActiveLayout(activeLayoutId: string): Promise<{ activeLayoutId: string }> {
  return jsonOrThrow(
    await authedFetch(`${BASE}/active`, { method: 'PUT', body: JSON.stringify({ activeLayoutId }) }),
    'set active layout',
  );
}

// The shared favorites API bound to the web transport (the dashboard-layout
// half above is web-only). Mobile binds the same factory.
export const { fetchFavorites, updateFavorites } = createPreferencesApi(authedFetch);

export type { CardId };

/**
 * The assistant's per-user preferences.
 *
 * `morningBriefing` is whether the brief is offered at all. It is an OPT-OUT —
 * the backend answers `true` for a user who has never saved anything — so the UI
 * must never show "off" while it is still loading.
 *
 * `briefInterest` and `mutedBriefSections` are what the brief should be ABOUT:
 * the reader's own words steer the model-written half, and a muted section leaves
 * their ledger and is never shown to the model. Empty and `[]` for almost
 * everybody, which is the default brief.
 */
export type AiAgentPreferences = {
  morningBriefing: boolean;
  briefInterest: string;
  mutedBriefSections: BriefSectionKey[];
};

/**
 * The answer, with any brief field it does not carry read as "nothing chosen".
 *
 * A server one version behind this page — mid-deploy, or a dev frontend pointed
 * at an older backend — answers `{ morningBriefing }` alone. Left as it came, the
 * settings dialog crashed on the missing instruction; read this way, it opens on
 * an empty one, which is exactly what that server stores.
 */
function withBriefDefaults(answer: Partial<AiAgentPreferences> & Pick<AiAgentPreferences, 'morningBriefing'>): AiAgentPreferences {
  return { briefInterest: '', mutedBriefSections: [], ...answer };
}

export async function fetchAiAgentPreferences(signal?: AbortSignal): Promise<AiAgentPreferences> {
  return withBriefDefaults(await jsonOrThrow(await authedFetch(AI_AGENT_BASE, { signal }), 'fetch assistant preferences'));
}

export async function updateMorningBriefing(enabled: boolean): Promise<AiAgentPreferences> {
  return withBriefDefaults(
    await jsonOrThrow(
      await authedFetch(AI_AGENT_BASE, {
        method: 'PUT',
        body: JSON.stringify({ morningBriefing: enabled }),
      }),
      'update assistant preferences',
    ),
  );
}

/** Page tours the user has dismissed, by tour id. */
const TOURS_BASE = '/api/me/tours';
type SeenToursResponse = { seen: string[] };

export async function fetchSeenTours(signal?: AbortSignal): Promise<string[]> {
  const res = await jsonOrThrow<SeenToursResponse>(await authedFetch(TOURS_BASE, { signal }), 'fetch seen tours');
  return res.seen;
}

/** Idempotent: the server answers the full list either way. */
export async function markTourSeen(tourId: string): Promise<string[]> {
  const res = await jsonOrThrow<SeenToursResponse>(
    await authedFetch(`${TOURS_BASE}/${encodeURIComponent(tourId)}/seen`, { method: 'PUT' }),
    'mark tour seen',
  );
  return res.seen;
}

/**
 * Save what the reader wants their morning brief to be about.
 *
 * Deliberately does NOT send `morningBriefing`: the endpoint treats an absent
 * field as "leave it", so the dialog cannot reset the daily switch and the switch
 * cannot wipe a saved paragraph. An empty `briefInterest`, though, IS sent and IS
 * a clear — that is how a reader deletes what they wrote.
 */
export async function updateBriefPreferences(update: {
  briefInterest?: string;
  mutedBriefSections?: BriefSectionKey[];
}): Promise<AiAgentPreferences> {
  return withBriefDefaults(
    await jsonOrThrow(
      await authedFetch(AI_AGENT_BASE, { method: 'PUT', body: JSON.stringify(update) }),
      'update brief preferences',
    ),
  );
}
